import type { GameLauncherUrlResult } from './game-url'
import { getGameLauncherUrlLegacyBti } from './game-url'

// Renew this early so a request never fires with a ticket about to expire.
const SAFETY_MARGIN_MS = 60 * 1000
// Fallback when the ticket carries no readable exp, so it still expires.
const FALLBACK_TTL_MS = 40 * 60 * 1000
// Sanity ceiling against a bogus far-future exp — a stale URL has no signal.
const MAX_TTL_MS = 24 * 60 * 60 * 1000

interface SportsLauncherArgs {
  isDesktop: boolean
  username: string
}

export const getSportsUrlCacheKey = (username: string, isDesktop: boolean) =>
  `funsports_play_url:${username || 'guest'}:${isDesktop ? 'pc' : 'mobile'}`

const ticketExpiresAtMs = (url: string): number | null => {
  try {
    const ticket = new URL(url).searchParams.get('ticket')
    const payload = ticket?.split('.')[1]
    if (!payload) return null
    const padded = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(payload.length / 4) * 4, '=')
    const exp = (JSON.parse(atob(padded)) as { exp?: unknown }).exp
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null
  } catch {
    return null
  }
}

export const readCachedSportsUrl = (key: string): string | null => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { url?: string; expiresAt?: number }
    if (!parsed.url || !parsed.expiresAt || parsed.expiresAt < Date.now()) {
      localStorage.removeItem(key)
      return null
    }
    return parsed.url
  } catch {
    return null
  }
}

export const writeCachedSportsUrl = (key: string, url: string) => {
  const now = Date.now()
  const hardExp = ticketExpiresAtMs(url)
  const expiresAt = Math.min(
    hardExp !== null ? hardExp - SAFETY_MARGIN_MS : now + FALLBACK_TTL_MS,
    now + MAX_TTL_MS,
  )
  // Already inside the safety margin — caching it would only serve a dead URL.
  if (expiresAt <= now) return
  try {
    localStorage.setItem(key, JSON.stringify({ url, expiresAt }))
  } catch {
    // ignore quota errors
  }
}

// Keyed by cache key so a prefetch and the lobby itself share one request.
const inFlight = new Map<string, Promise<GameLauncherUrlResult>>()

// Never rejects: both callers treat a failure as "no URL yet".
export const fetchSportsLauncherUrl = (
  args: SportsLauncherArgs,
): Promise<GameLauncherUrlResult> => {
  const key = getSportsUrlCacheKey(args.username, args.isDesktop)
  const pending = inFlight.get(key)
  if (pending) return pending

  const request = getGameLauncherUrlLegacyBti(args)
    .then(result => {
      if (result.status === 1 && result.url)
        writeCachedSportsUrl(key, result.url)
      return result
    })
    .catch(() => ({ status: 0 }) as GameLauncherUrlResult)
    .finally(() => inFlight.delete(key))

  inFlight.set(key, request)
  return request
}

// Fills the cache ahead of the first /sports visit; a fresh entry is left alone.
export const prefetchSportsLauncherUrl = async (args: SportsLauncherArgs) => {
  const key = getSportsUrlCacheKey(args.username, args.isDesktop)
  if (readCachedSportsUrl(key)) return
  await fetchSportsLauncherUrl(args)
}
